/**
 * registry.js — the stall implementation registry.
 *
 * Deliberately tiny and dependency-light: both `framework.js` (StallBase) and
 * `manager.js` (the round lifecycle) need it, and putting it here is what keeps
 * those two from importing each other in a cycle.
 *
 * Stall modules do not import this directly — they call `registerStall` from
 * `./framework.js`, which re-exports it.
 *
 * @module stalls/registry
 */

import { STALL_BY_ID, EVENTS } from '../constants.js';
import { bus } from '../core/events.js';

/** @type {Map<string, {id: string, create: Function, def: object}>} */
export const registry = new Map();

/**
 * Register a stall implementation. Call this at module scope; the module gets
 * pulled in by the manifest in `src/stalls/index.js`.
 *
 * @param {{ id: string, create: (ctx: object) => object, name?: string,
 *           cost?: number, profile?: object }} def
 */
export function registerStall(def) {
  if (!def || !def.id) throw new Error('registerStall: def.id is required');
  if (!STALL_BY_ID[def.id]) {
    throw new Error(`registerStall: "${def.id}" is not one of the 12 GDD stalls`);
  }
  if (registry.has(def.id)) {
    console.warn(`[stalls] "${def.id}" registered twice — keeping the first`);
    return;
  }
  registry.set(def.id, { id: def.id, create: def.create, def });
  bus.emit(EVENTS.STALL_REGISTERED, { id: def.id });
}

/** @param {string} id @returns {boolean} */
export function isRegistered(id) {
  return registry.has(id);
}

/** @returns {string[]} Ids that have a real implementation. */
export function registeredStalls() {
  return [...registry.keys()];
}
